// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { MetricBottomSummary } from '@lfx/key-projects/key-projects.model';
import { BaseMetricEntity, BaseMetricConfig, ChartType, ScoreLevel } from './base-metric.entity';

/**
 * Configuration interface for EventAttendance entity
 */
type EventAttendanceConfig = BaseMetricConfig;

/**
 * EventAttendance Entity - Encapsulates all event attendance-specific business logic
 *
 * This class consolidates configuration, scoring rules, and display logic
 * for the event attendance ecosystem metric.
 */
export class EventAttendance extends BaseMetricEntity {
  static readonly METRIC_TYPE = 'event_attendance' as const;

  /**
   * Centralized configuration object containing all static values
   */
  protected static readonly CONFIG: EventAttendanceConfig = {
    metricType: 'event_attendance',
    display: {
      displayName: 'Event Attendance',
      chartType: 'area' as ChartType,
      tooltip: {
        definition: 'What percentage of events do employees from our company attend?',
        scoringRules: [
          '0 events: = 0 points',
          '0.1 to 25% of events = 0.33 points',
          '25.1 to 50% of events = 0.66 points',
          '>50% of events = 1 point',
          '0 points = <b>Silent</b>',
          '0.33 points = <b>Participating</b>',
          '0.66 points = <b>Contributing</b>',
          '1 point = <b>Leading</b>'
        ]
      }
    },
    bottomSummary: {
      zeroValue: {
        description: `Our company has no event attendance for {context}.`,
      },
      positiveValue: {
        descriptionTemplate: `Employees from our company attended {percentage}% of all events for {context}.`,
      },
      nullValue: {
        description: this.NULL_VALUE_DESCRIPTION,
      }
    }
  };

  /**
   * Get display configuration from config object
  */
  public static getDisplayConfig(): BaseMetricConfig['display'] {
      return this.CONFIG.display;
  }

  /**
   * Get full configuration object
   */
  public static getConfig(): BaseMetricConfig {
      return this.CONFIG;
  }

  /**
   * Get bottom summary configuration for event attendance
   * Handles null case for no data available
   */
  static getBottomSummary(percentage: string, allTimeTotal: number, isLfProject: boolean, level: ScoreLevel, projectType?: string | null): MetricBottomSummary {
    if (isLfProject === false) {
      return this.getNonLfProjectBottomSummary(this.CONFIG);
    }

    if (allTimeTotal === 0) {
      return this.getNullValueBottomSummary(this.CONFIG);
    }

    const percentageValue = percentage || '0.0';
    const numericValue = parseFloat(percentageValue);
    const styles = this.getStylesByLevel(level);
    const contextText = projectType?.toLowerCase() === 'foundation'
      ? 'this foundation'
      : 'this project\'s foundation';

    const description = numericValue === 0
      ? this.formatDescription(this.CONFIG.bottomSummary.zeroValue.description, {context: contextText})
      : this.formatDescription(this.CONFIG.bottomSummary.positiveValue.descriptionTemplate, {percentage: percentageValue, context: contextText});

    return {
      title: this.CONFIG.display.displayName,
      description: description,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor
    };
  }

}
